import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';

import * as moment from 'moment';
import {Moment} from 'moment';

// export const ONE_DAY_MILLISECONDS = 24 * 1000 * 60 * 60;
// export const EPOCH_START_DATE = Date.UTC(1900, 0, 1);
// export const EPOCH_START_DATE = new Date(1900, 0, 1);

const oneSecondMilliseconds = 1000;
const oneMinuteMilliseconds = 60 * oneSecondMilliseconds;
const oneHourMilliseconds = 60 * oneMinuteMilliseconds;
const oneDayMilliseconds = 24 * oneHourMilliseconds;

export class ADateTime extends BaseValue
{
    @serializable public readonly year:number;
    @serializable public readonly month:number;
    @serializable public readonly day:number;
    @serializable public readonly hours:number;
    @serializable public readonly minutes:number;
    @serializable public readonly seconds:number;

    public constructor(year:number,
                       month:number,
                       day:number,
                       hours:number,
                       minutes:number,
                       seconds:number)
    {
        super(Type.DateTime);

        this.year = year;
        this.month = month;
        this.day = day;
        this.hours = hours;
        this.minutes = minutes;
        this.seconds = seconds;
    }

    public toString():string
    {
        return this.year != null && this.month != null && this.day != null && this.hours != null && this.minutes != null && this.seconds != null
            ? `${this.year}-${this.month}-${this.day}T${this.hours}:${this.minutes}:${this.seconds}`
            : null;
    }

    public toValue():string
    {
        return `{"value":"${this.year}-${this.month}-${this.day}T${this.hours}:${this.minutes}:${this.seconds}"}`;
    }

    public getTime():number
    {
        return this.getDate().getTime();
    }

    public getDate():Date
    {
        return (new Date(this.year, this.month - 1, this.day, this.hours, this.minutes, this.seconds));
    }

    public isEqualTo(other:IValue):boolean
    {
        return other &&
            Type.isInstanceOf(other, Type.DateTime) &&
            this.getTime() === (other as ADateTime).getTime();
    }

    public compareTo(other:IValue):number
    {
        if( !other ) return 1;
        if( Type.isInstanceOf(other, Type.Error) || Type.isInstanceOf(other, Type.Empty) )
        {
            return 1;
        }
        if( this.isEqualTo(other) )
            return 0;

        if( Type.isInstanceOf(other, Type.DateTime) )
        {
            return (this.getTime() > (other as ADateTime).getTime()) ? 1 : -1;
        }

        return -1;
    }

    public clone():IValue
    {
        return new ADateTime(this.year, this.month, this.day, this.hours, this.minutes, this.seconds);
    }

    static fromJSDate(value:Date):ADateTime
    {
        const year = value.getFullYear();
        const month = value.getMonth() + 1;
        const day = value.getDate();
        const hours = value.getHours();
        const minutes = value.getMinutes();
        const seconds = value.getSeconds();
        return new ADateTime(year, month, day, hours, minutes, seconds);
    }

    public toMoment():Moment
    {
        const {year, month, day, hours, minutes, seconds} = this;
        return moment({years: year, months: month - 1, days: day, hours, minutes, seconds});
    }

    public toJSDate():Date
    {
        return this.toMoment().toDate();
    }

    public static circularRefResolver(value:string):IValue
    {
        // KEEP_ALIVE! Fix for resolving import circular dependencies for AError and ABaseValue
        return new AError('ERROR: Deprecated');
    }

    public toExcelDate():number
    {
        return ADateTime._daysFrom1900(this.getDate());
    }

    public static _daysFrom1900(date:Date):number
    {
        //example: we have a date 25-06-2019 04:03:25
        // let addOn = (date.valueOf() > -2203891200000) ? 1 : 2;
        // let days = Math.ceil(((date.valueOf() - EPOCH_START_DATE.valueOf()) / ONE_DAY_MILLISECONDS) + addOn); //integer part, it will be 43641
        // let fractional = (date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds()) / 86400; // e.g. 04:03:25 will be 14605/86400=0.169039
        // return days + fractional; //result 43641.169039

        const returnDateTime = 25569.0 + ((date.getTime() - (date.getTimezoneOffset() * oneMinuteMilliseconds)) / oneDayMilliseconds);

        return returnDateTime;
    }
}
